import { type DateRange, getRange } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeesAllRoute } from "../../app-routes";
import type { EmployeesAllLayoutRoute } from "./employees-all.route";
import { advancesQuery, documentTagsQuery, documentsQuery } from "./records.queries";

export function createRecordsRoutes(parent: EmployeesAllLayoutRoute) {
    const profile = createRoute({
        getParentRoute: () => parent,
        path: "core-data",
        staticData: {
            title: "Stammdaten: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/stammdaten",
            menu: employeesAllRoute("core-data"),
        },
        component: (): TemplateResult =>
            html`<ptc-employees-profile-all class="fullbleed"></ptc-employees-profile-all>`,
    }).lazy(lazyElement(() => import("./employees-profile-all")));

    const positions = createRoute({
        getParentRoute: () => parent,
        path: "positions",
        staticData: {
            title: "Positionen: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/positionen",
            menu: employeesAllRoute("positions"),
        },
        component: (): TemplateResult =>
            html`<ptc-employees-positions-all class="fullbleed"></ptc-employees-positions-all>`,
    }).lazy(lazyElement(() => import("./employees-positions-all")));

    const permissions = createRoute({
        getParentRoute: () => parent,
        path: "permissions",
        staticData: {
            title: "Berechtigungen: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/zugaenge-rechte",
            menu: employeesAllRoute("permissions"),
        },
        // Refreshes app state, which the page reads; nothing to hand over.
        loader: async ({ context }): Promise<void> => {
            await context.app.fetchCompany();
        },
        component: (): TemplateResult =>
            html`<ptc-employees-permissions-all class="fullbleed"></ptc-employees-permissions-all>`,
    }).lazy(lazyElement(() => import("./employees-permissions-all")));

    const documents = createRoute({
        getParentRoute: () => parent,
        path: "documents",
        staticData: {
            title: "Dokumente: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/dokumente",
            menu: employeesAllRoute("documents"),
        },
        // Either call may fail without taking the page down.
        loader: async ({ context }) => {
            const [documents, documentTags] = await Promise.allSettled([
                context.queryClient.fetchQuery({ ...documentsQuery(context.app), staleTime: 30_000 }),
                context.queryClient.fetchQuery({ ...documentTagsQuery(context.app), staleTime: 30_000 }),
            ]);
            return {
                documents: documents.status === "fulfilled" ? documents.value : [],
                documentTags: documentTags.status === "fulfilled" ? documentTags.value : [],
            };
        },
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-documents-all
                class="fullbleed"
                .documents=${match.loaderData?.documents ?? []}
                .documentTags=${match.loaderData?.documentTags ?? []}
            ></ptc-employees-documents-all>`,
    }).lazy(lazyElement(() => import("./employees-documents-all")));

    const advances = createRoute({
        getParentRoute: () => parent,
        path: "advances",
        staticData: {
            title: "Vorschüsse: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/vorschuesse",
            menu: employeesAllRoute("advances"),
        },
        validateSearch: (search: Record<string, unknown>): Partial<DateRange> => ({
            from: search.from as DateString | undefined,
            to: search.to as DateString | undefined,
        }),
        beforeLoad: ({ search }): void => {
            if (!search.from || !search.to) {
                redirectTo("/employees/all/advances", { ...search, ...getRange(new Date(), "month") });
            }
        },
        loaderDeps: ({ search }) => ({ from: search.from!, to: search.to! }),
        loader: ({ deps, context }) =>
            context.queryClient.fetchQuery({ ...advancesQuery(context.app, deps), staleTime: 30_000 }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-advances-all
                class="fullbleed"
                .entries=${match.loaderData ?? []}
                .range=${{ from: match.search.from, to: match.search.to }}
            ></ptc-employees-advances-all>`,
    }).lazy(lazyElement(() => import("./employees-advances-all")));

    return [profile, positions, permissions, documents, advances] as const;
}
